type ScoreColor = "green" | "orange" | "red";

export interface ScoreData {
  label: string;
  value: number;
  color?: ScoreColor;
}

export function getScoreColor(value: number): ScoreColor {
  if (value >= 90) return "green";
  if (value >= 50) return "orange";
  return "red";
}

export interface WebVital {
  abbr: string;
  label: string;
  value: string;
  color: ScoreColor;
}

interface Props {
  scores: ScoreData[];
  vitals: WebVital[];
  timestamp: string;
  locale: "de" | "en";
}

// Calvarius-tinted scoring palette. WCAG-AA contrast on white background.
const HEX: Record<ScoreColor, { stroke: string; track: string; text: string }> = {
  green: { stroke: "#6F9020", track: "#F2F8DF", text: "#4F6817" }, // lime-deep
  orange: { stroke: "#C97A1B", track: "#FBEFD8", text: "#9A5C12" },
  red: { stroke: "#B23A2E", track: "#FBE2DD", text: "#8A2A20" },
};

function Donut({ value, color, ariaLabel }: { value: number; color: ScoreColor; ariaLabel: string }) {
  const size = 80;
  const stroke = 7;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const palette = HEX[color];
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      className="block"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={palette.track} strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={palette.stroke}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
        fontWeight={600}
        fontSize="22"
        fill={palette.text}
      >
        {value}
      </text>
    </svg>
  );
}

export function LighthouseScoreDisplay({ scores, vitals, timestamp, locale }: Props) {
  const auditLabel = locale === "de" ? "Lighthouse-Audit" : "Lighthouse audit";
  return (
    <figure
      className="not-prose my-10 rounded-lg border border-rule bg-cyan-tint-2 p-6 md:p-8"
      aria-label={auditLabel}
    >
      <figcaption className="sr-only">{timestamp}</figcaption>

      {/* Score donuts */}
      <ul className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-4">
        {scores.map((s) => (
          <li key={s.label} className="flex flex-col items-center gap-3 text-center">
            <Donut value={s.value} color={s.color ?? getScoreColor(s.value)} ariaLabel={`${s.label}: ${s.value}`} />
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-3">
              {s.label}
            </span>
          </li>
        ))}
      </ul>

      {/* Web Vitals */}
      <div className="mt-8 grid grid-cols-2 gap-3 border-t border-rule pt-6 sm:grid-cols-4">
        {vitals.map((v) => (
          <div key={v.abbr} className="flex items-start gap-2">
            <span
              className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: HEX[v.color].stroke }}
              aria-hidden
            />
            <div className="min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3">
                {v.abbr} · {v.value}
              </div>
              <div className="text-xs text-ink-4 leading-snug">{v.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Stamp */}
      <div className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-ink-4">
        {timestamp}
      </div>
    </figure>
  );
}
